import classnames from 'classnames'
import PropTypes from 'prop-types'
import { Component } from 'react'
import ReactDOM from 'react-dom/client'

import { TIMEFRAME } from '../../main/constants'
import { apiRequest } from '../../main/services/api'
import { decodeQueryString, encodeQueryString } from '../queryString'
import { redGreenGradient } from '../shared/gradients'
import * as hoverChart from '../shared/hover-chart'
import FuturesActionCreators from './actions/futuresActionCreators'
import BarChart from './components/BarChart'
import CanvasSparkline from './components/CanvasSparkline'
import { ChartMenu } from './components/ChartMenu'
import { Layout as NewLayout } from './components/Layout'
import Menu from './components/Menu'
import { MenuNew } from './components/MenuNew'
import RowsStore from './stores/rowsStore'
import TilesStore from './stores/tilesStore'
import { FuturesTimeframe, LabelStyle, LayoutType, PageType, TileStyle } from './types'
import { FuturesDatum, FuturesRow } from './types'
import { futuresDomains } from './utils'
import { generateColorScale } from './utils'

var colorScale = generateColorScale(futuresDomains, 'd')

const SMALL_BREAKPOINT_WIDTH = 1280

var bigLayout = [
  ['YM', 'ES', 'NQ', 'ER2', 'NKD', 'EX', 'DY', 'VX'],
  ['CL', 'QA', 'RB', 'HO', 'NG', 'ZK', 'ZB', 'ZN'],
  ['CC', 'CT', 'JO', 'KC', 'LB', 'SB', 'ZF', 'ZT'],
  ['GC', 'SI', 'PL', 'HG', 'PA', 'LC', 'FC', 'LH'],
  ['ZS', 'ZM', 'ZL', 'ZC', 'ZW', 'ZR', 'ZO', 'RS'],
  ['DX', '6E', '6J', '6B', '6C', '6S', '6A', '6N'],
]

var bigLayoutFirstInGroup = [
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 0, 1, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 1, 0, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
  [1, 0, 0, 0, 0, 0, 0, 0],
]

var smallLayout = [
  ['YM', 'ES', 'NQ', 'ER2', 'ZB', 'ZN'],
  ['NKD', 'EX', 'DY', 'VX', 'ZF', 'ZT'],
  ['CL', 'QA', 'RB', 'HO', 'NG', 'ZK'],
  ['CC', 'CT', 'JO', 'KC', 'LB', 'SB'],
  ['ZS', 'ZM', 'ZL', 'ZC', 'GC', 'SI'],
  ['ZW', 'ZR', 'ZO', 'RS', 'PL', 'HG'],
  ['DX', '6E', '6J', '6B', 'PA', 'LH'],
  ['6C', '6S', '6A', '6N', 'LC', 'FC'],
]

function getAdvancedTileState(ticker: string) {
  var tile = TilesStore.getTile(ticker)
  var group = TilesStore.getTilesGroup(tile.ticker).toLowerCase()

  return {
    ...tile,
    group,
  }
}

interface AdvancedTileProps {
  width: number
  ticker: string
  hoverOffsetX: number
  hoverOffsetY: number
}

class AdvancedTile extends Component<AdvancedTileProps> {
  static propTypes = {
    ticker: PropTypes.string,
  }

  state = getAdvancedTileState(this.props.ticker)

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getAdvancedTileState(this.props.ticker))
  }

  render() {
    var { ticker, label, last, prevClose, change, sparkline, sparklineDateChanges, group } = this.state
    var { hoverOffsetX, hoverOffsetY } = this.props

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    var places = last > 6 ? 2 : 4
    var round = (n: number, plusSign?: string) =>
      (n >= 0 ? plusSign || '' : '') + (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      FinvizSettings.hoverCharts?.futures?.timeframe,
      FinvizSettings.hoverCharts?.futures?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    return (
      <a
        className="tile"
        href={'/futures_charts.ashx?p=d&t=' + ticker}
        style={{ width: this.props.width }}
        data-boxover={boxover}
      >
        <div className={'tile_header is-' + group}>
          <span className="tile_label pull-left">{label}</span>
          <span className="tile_change pull-right">{round(last - prevClose, '+')}</span>
        </div>
        <div className="tile_content" style={{ backgroundColor: colorScale(change) }}>
          <span className="tile_last-price">{round(last)}</span>
          <span className="tile_change-pct pull-right">{format(change)}</span>
          <CanvasSparkline
            width={this.props.width}
            height={49}
            data={sparkline}
            dateChanges={sparklineDateChanges}
            change={change}
            changeDomain={1.3}
          />
        </div>
      </a>
    )
  }
}

function getSimpleTileState(ticker: string) {
  var tile = TilesStore.getTile(ticker)
  var group = TilesStore.getTilesGroup(tile.ticker).toLowerCase()

  return {
    ...tile,
    group,
  }
}

interface SimpleTileProps {
  width: number
  ticker: string
  hoverOffsetX: number
  hoverOffsetY: number
  firstInGroup: unknown
  lastInGroup: unknown
}

class SimpleTile extends Component<SimpleTileProps> {
  static propTypes = {
    ticker: PropTypes.string,
  }

  state = getSimpleTileState(this.props.ticker)

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getSimpleTileState(this.props.ticker))
  }

  render() {
    var { ticker, label, last, prevClose, change, high, low, group } = this.state
    var { hoverOffsetX, hoverOffsetY, firstInGroup, lastInGroup } = this.props

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    var places = last > 6 ? 2 : 4
    var round = (n: number, plusSign?: string) =>
      (n >= 0 ? plusSign || '' : '') + (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    var range = (high - low) * 0.1
    var highlightHigh = last > high - range
    var highlightLow = last < low + range
    var highlightStyle = {
      color: '#fbdb19',
    }

    var opacityChange = Math.min(Math.abs(change), 1.3)
    var textOpacity = 0.5 + (0.2 / 1.3) * opacityChange

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      FinvizSettings.hoverCharts?.futures?.timeframe,
      FinvizSettings.hoverCharts?.futures?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    return (
      <a
        className="simple-tile"
        href={'/futures_charts.ashx?p=d&t=' + ticker}
        style={{ width: this.props.width }}
        data-boxover={boxover}
      >
        <div
          className={'tile_header is-' + group}
          style={{ width: lastInGroup ? this.props.width : this.props.width + 2 }}
        >
          {firstInGroup ? group.toUpperCase() : ''}
        </div>
        <div className="tile_content" style={{ backgroundColor: colorScale(change) }}>
          <div className="tile_label">{label}</div>
          <div className="tile_last-price">{round(last)}</div>

          <div className="tile_high-low pull-left" style={{ color: `rgba(255, 255, 255, ${textOpacity})` }}>
            <div style={highlightHigh ? highlightStyle : {}}>
              <span className="tile_high-label">H</span> {round(high)}
            </div>
            <div style={highlightLow ? highlightStyle : {}}>
              <span className="tile_low-label">L</span> {round(low)}
            </div>
          </div>

          <div className="tile_change pull-right" style={{ color: `rgba(255, 255, 255, ${textOpacity})` }}>
            {round(last - prevClose, '+')}
            <br />({format(change)})
          </div>
        </div>
      </a>
    )
  }
}

class AdvancedLayout extends Component {
  render() {
    var w = Math.min(document.documentElement.clientWidth, window.innerWidth || 0)

    var layout = w >= SMALL_BREAKPOINT_WIDTH ? bigLayout : smallLayout

    var tileWidth = 156
    if (w < 1304 && w >= SMALL_BREAKPOINT_WIDTH) {
      tileWidth = 150
    }

    var cols = layout[0].length
    var rows = layout.length
    return (
      <div style={{ margin: '0 auto', width: (tileWidth + 2) * layout[0].length }}>
        {layout.map((row, ri) => (
          <div key={ri}>
            {row.map((ticker, ci) => (
              <AdvancedTile
                ticker={ticker}
                key={ticker}
                width={tileWidth}
                hoverOffsetX={ci < cols / 2 ? 0 : -320}
                hoverOffsetY={ri < rows / 2 ? 25 : -205}
              />
            ))}
            <div className="clearfix"></div>
          </div>
        ))}
      </div>
    )
  }
}

class SimpleLayout extends Component {
  render() {
    var w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)

    var layout = bigLayout
    var firstInGroup = bigLayoutFirstInGroup

    var tileWidth = w >= SMALL_BREAKPOINT_WIDTH ? 136 : 120

    var cols = layout[0].length
    var rows = layout.length
    return (
      <div style={{ margin: '0 auto', width: (tileWidth + 2) * layout[0].length }}>
        {layout.map((row, ri) => (
          <div key={ri}>
            {row.map((ticker, ci) => (
              <SimpleTile
                ticker={ticker}
                key={ticker}
                width={tileWidth}
                hoverOffsetX={ci < cols / 2 ? 0 : -320}
                hoverOffsetY={ri < rows / 2 ? 25 : -205}
                firstInGroup={firstInGroup[ri][ci]}
                // eslint-disable-next-line eqeqeq
                lastInGroup={TilesStore.getTilesGroup(ticker) != TilesStore.getTilesGroup(layout[ri][ci + 1])}
              />
            ))}
            <div className="clearfix"></div>
          </div>
        ))}
      </div>
    )
  }
}

function getFuturesState() {
  return {
    activeTimeframe: RowsStore.getTimeframe(),
    rows: RowsStore.getRows(),
  }
}

class Futures extends Component {
  state = getFuturesState()

  componentDidMount() {
    RowsStore.addChangeListener(this._onChange)
  }

  componentWillUnmount() {
    RowsStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState(getFuturesState())
  }

  render() {
    const { activeTimeframe, rows } = this.state

    var timeframes = {
      onClick: (timeframe: string, e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
        FuturesActionCreators.changeTimeframe(timeframe)
      },
      active: activeTimeframe,
      timeframes: [
        { key: FuturesTimeframe.None, value: <span>&Oslash;</span> },
        { key: FuturesTimeframe.Minute5, value: '5M' },
        { key: FuturesTimeframe.Hourly, value: 'H' },
        { key: FuturesTimeframe.Daily, value: 'D' },
        { key: FuturesTimeframe.Weekly, value: 'W' },
        { key: FuturesTimeframe.Monthly, value: 'M' },
      ],
    }

    if (FinvizSettings.hasRedesignEnabled) {
      return (
        <div className="futures flex flex-col">
          <MenuNew activePage={PageType.Quotes} timeframes={timeframes} />
          <NewLayout
            activeTimeframe={timeframes.active}
            showSparkline={activeTimeframe !== 'NO'}
            labelStyle={LabelStyle.Side}
            tileStyle={TileStyle.MapCondensed}
            type={LayoutType.NewHorizontal}
          />
          <div className="perf-chart-container mt-7">
            <BarChart
              width={740}
              data={rows}
              onClick={this._onBarClick}
              period="d"
              gap={1}
              gradient={redGreenGradient}
              isWrapper
            />
          </div>
        </div>
      )
    }

    return (
      <div className="futures flex flex-col">
        <Menu activePage={PageType.Quotes} timeframes={timeframes} />

        {activeTimeframe === 'NO' ? <SimpleLayout /> : <AdvancedLayout />}
        <div className="clearfix" />
        <br />
        <div className="perf-chart-container">
          <BarChart width={740} data={rows} onClick={this._onBarClick} period="d" />
        </div>
      </div>
    )
  }

  _onBarClick = (bar: { label: string }) => {
    var ticker = TilesStore.getTilesList().filter(function (tile) {
      return tile.label === bar.label
    })[0].ticker
    window.open(`/futures_charts.ashx?t=${ticker}&p=d`)
  }
}

interface FuturesPerformanceProps {
  rows: FuturesRow[]
}

class FuturesPerformance extends Component<FuturesPerformanceProps> {
  declare refreshInterval: number

  state = {
    rows: this.props.rows,
    hoveredGroup: null,
  }

  componentDidMount() {
    this.refreshInterval = window.setInterval(async () => {
      const data = await apiRequest(`/api/futures_perf.ashx${window.location.search}`)
      if (data) {
        this.setState({ rows: data })
      }
    }, 30 * 1000)
  }

  componentWillUnmount() {
    clearInterval(this.refreshInterval)
  }

  render() {
    var view = (decodeQueryString()['v'] || '11').substring(1, 2)

    var kv = { d: 1, w: 2, m: 3, mtd: 8, q: 4, hy: 5, y: 6, ytd: 7 }
    var vk: Record<string, keyof typeof kv> = {}
    Object.keys(kv).forEach((key: any) => {
      vk[kv[key as keyof typeof kv].toString()] = key
    })
    var timeframes = {
      link: (key: string) => '/futures_performance.ashx?v=1' + kv[key as keyof typeof kv],
      onClick: () => {},
      active: view && vk[view],
      timeframes: [
        { key: 'd', value: 'D', title: 'Day' },
        { key: 'w', value: 'W', title: 'Week' },
        { key: 'm', value: 'M', title: 'Month' },
        { key: 'mtd', value: 'MTD', title: 'Month to Date' },
        { key: 'q', value: 'Q', title: 'Quarter' },
        { key: 'hy', value: 'HY', title: 'Half Year' },
        { key: 'y', value: 'Y', title: 'Year' },
        { key: 'ytd', value: 'YTD', title: 'Year To Date' },
      ],
    }

    var hoveredGroup = this.state.hoveredGroup
    var rows = this.state.rows
      .map(function (row) {
        return {
          label: row.label,
          value: row.perf,
          isInactive: hoveredGroup !== null && hoveredGroup !== row.group,
        }
      })
      .sort(function (a, b) {
        return b.value - a.value
      })

    const MenuComponent = FinvizSettings.hasRedesignEnabled ? MenuNew : Menu

    return (
      <div className="futures flex flex-col">
        <MenuComponent
          activePage={PageType.Performance}
          timeframes={timeframes}
          onGroupMouseEnter={(group) => {
            this.setState({ hoveredGroup: group })
          }}
          onGroupMouseLeave={() => {
            this.setState({ hoveredGroup: null })
          }}
        />
        <div className="perf-chart-container">
          <BarChart
            width={740}
            data={rows}
            onClick={this._onBarClick}
            period={vk[view]}
            gradient={FinvizSettings.hasRedesignEnabled ? redGreenGradient : undefined}
            isWrapper
          />
        </div>
      </div>
    )
  }

  _onBarClick = (bar: { label: string }) => {
    var ticker = this.state.rows.filter(function (tile) {
      return tile.label === bar.label
    })[0].ticker
    window.open(`/futures_charts.ashx?t=${ticker}&p=d`)
  }
}

interface ChartsState {
  timeframe: TIMEFRAME
  dateRange: string
  ticker: string
  type: string
  isGroupOpen: boolean
  activeGroup: string
}

function getChartsState(override: Partial<ChartsState> = {}) {
  var queryString = decodeQueryString()
  var timeframe = queryString['p'] as TIMEFRAME
  if (!Object.values(TIMEFRAME).includes(timeframe)) {
    timeframe = TIMEFRAME.d
  }
  var ticker = (queryString['t'] || 'ALL').toUpperCase()
  var type = queryString['ty']

  var isGroupOpen = ticker === 'ALL' || ticker.length > 3
  if (!isGroupOpen && !TilesStore.getTilesGroup(ticker)) {
    ticker = 'ALL'
    isGroupOpen = true
  }
  var activeGroup = (isGroupOpen ? ticker : TilesStore.getTilesGroup(ticker)).toLowerCase()

  return {
    timeframe,
    dateRange: queryString['r'] || undefined,
    ticker,
    type,
    isGroupOpen,
    activeGroup,
    ...override,
  }
}

export class FuturesCharts extends Component<any, ReturnType<typeof getChartsState>> {
  state = getChartsState()

  _onTimeframeChange = async (timeframe: TIMEFRAME) => {
    FuturesActionCreators.changeTimeframe(timeframe, false)
    this.setState(getChartsState({ timeframe }))
  }

  render() {
    const { timeframe, ticker, type, isGroupOpen, activeGroup, dateRange } = this.state

    var timeframes = {
      link: (key: string) => `/futures_charts.ashx?${encodeQueryString({ t: ticker, p: key, ty: type, r: dateRange })}`,
      onClick: () => {},
      active: timeframe,
      timeframes: [
        { key: FuturesTimeframe.Minute5, value: '5M' },
        { key: FuturesTimeframe.Hourly, value: 'H' },
        { key: FuturesTimeframe.Daily, value: 'D' },
        { key: FuturesTimeframe.Weekly, value: 'W' },
        { key: FuturesTimeframe.Monthly, value: 'M' },
      ],
    }

    const isRedesignEnabled = FinvizSettings.hasRedesignEnabled
    const MenuComponent = isRedesignEnabled ? MenuNew : Menu

    return (
      <div
        className={classnames('flex flex-col', {
          futures: !isRedesignEnabled,
          'pb-5': !isGroupOpen && isRedesignEnabled,
        })}
      >
        <div
          className={classnames({
            'border-b border-gray-100 bg-white dark:border-gray-600 dark:bg-gray-800':
              isRedesignEnabled && !isGroupOpen,
          })}
        >
          <div
            className={classnames(
              'mx-auto',
              isRedesignEnabled && {
                'max-w-[79rem]': isGroupOpen,
                'max-w-[81.5rem] px-5': !isGroupOpen,
              }
            )}
          >
            <MenuComponent
              activePage={PageType.Charts}
              activeGroup={activeGroup}
              activeRange={dateRange}
              isDetailOpen={!isGroupOpen}
              groupTimeframe={timeframe}
              timeframes={timeframes}
              type={type}
              company={TilesStore.getTile(ticker)?.label}
              onTimeframeChange={this._onTimeframeChange}
              onDateRangeChange={(dateRange) =>
                this.setState((prevState) => getChartsState({ dateRange, timeframe: prevState.timeframe }))
              }
            />
          </div>
        </div>
        {!isGroupOpen && (
          <ChartMenu
            isRedesignEnabled={isRedesignEnabled}
            activeGroup={activeGroup}
            activeTile={ticker}
            activeTimeframe={timeframe}
            activeRange={dateRange}
            type={type}
            showChartControls={!isRedesignEnabled}
          />
        )}
      </div>
    )
  }
}
window['FinvizInitFutures'] = function (groups: FuturesDatum[]) {
  FuturesActionCreators.init(groups, 'quotes')
  const rootEl = document.getElementById('futures')
  const root = ReactDOM.createRoot(rootEl!)
  root.render(<Futures />)

  setInterval(FuturesActionCreators.update, 30 * 1000)
}

window['FinvizInitFuturesPerformance'] = function (rows: FuturesRow[]) {
  const rootEl = document.getElementById('futures')
  const root = ReactDOM.createRoot(rootEl!)
  root.render(<FuturesPerformance rows={rows} />)
}

// @ts-ignore
window.RowsStore = RowsStore
// @ts-ignore
window.TilesStore = TilesStore
